import { Activity, Brain, ChevronRight, CircleAlert, Send, Wrench } from "lucide-react"
import * as React from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"

import { EmptyState } from "@/components/empty-state"
import { JsonOrText, JsonViewer } from "@/components/json-viewer"
import { PageHeader } from "@/components/layout"
import { RUN_STATUS_LABEL_KEYS, RunStatusBadge } from "@/components/status-badge"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { useLocale, useT } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import { useAuth } from "@/lib/auth"
import type { Agent, Run, RunStatus, RunTrace, TelegramChatRef } from "@/lib/types"
import { cn, formatCostUsd, formatDateTime, formatDuration } from "@/lib/utils"

const STATUSES: RunStatus[] = [
  "queued",
  "running",
  "succeeded",
  "failed",
  "cancelled",
  "max_iterations",
  "timeout",
  "abandoned",
]
const TRIGGERS = ["chat", "schedule", "telegram"]
//: The Select component reserves "" for "no value", so "any" stands in for no filter.
const ANY = "any"

function telegramChatLabel(chat: TelegramChatRef): string {
  return chat.title ? chat.title : String(chat.chat_id)
}

export function RunsPage() {
  const t = useT()
  const { locale } = useLocale()
  const { runId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = user?.is_admin ?? false
  const [runs, setRuns] = React.useState<Run[]>([])
  const [agents, setAgents] = React.useState<Agent[]>([])
  const [telegramChats, setTelegramChats] = React.useState<TelegramChatRef[]>([])
  const [loading, setLoading] = React.useState(true)

  const [agentFilter, setAgentFilter] = React.useState(ANY)
  const [statusFilter, setStatusFilter] = React.useState(ANY)
  const [triggerFilter, setTriggerFilter] = React.useState(ANY)
  const [chatFilter, setChatFilter] = React.useState(ANY)

  React.useEffect(() => {
    api.listAgents().then(setAgents).catch(() => undefined)
    api.listTelegramChats().then(setTelegramChats).catch(() => undefined)
  }, [])

  React.useEffect(() => {
    setLoading(true)
    api
      .listRuns({
        agentId: agentFilter === ANY ? undefined : agentFilter,
        status: statusFilter === ANY ? undefined : statusFilter,
        trigger: triggerFilter === ANY ? undefined : triggerFilter,
        telegramChatId: chatFilter === ANY ? undefined : Number(chatFilter),
      })
      .then(setRuns)
      .catch((error) =>
        toast.error(error instanceof ApiError ? error.message : t("runs.toasts.loadFailed")),
      )
      .finally(() => setLoading(false))
  }, [agentFilter, statusFilter, triggerFilter, chatFilter, t])

  if (runId) return <RunDetailView runId={runId} onBack={() => navigate("/runs")} />

  return (
    <>
      <PageHeader
        title={t("runs.title")}
        description={isAdmin ? t("runs.description.admin") : t("runs.description.user")}
      />
      <div className="p-6">
        <div className="mb-4 flex flex-wrap gap-2">
          <Select value={agentFilter} onValueChange={setAgentFilter}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>{t("runs.filters.allAgents")}</SelectItem>
              {agents.map((agent) => (
                <SelectItem key={agent.id} value={agent.id}>
                  {agent.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>{t("runs.filters.allStatuses")}</SelectItem>
              {STATUSES.map((status) => (
                <SelectItem key={status} value={status}>
                  {t(`common.status.run.${RUN_STATUS_LABEL_KEYS[status] ?? status}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={triggerFilter} onValueChange={setTriggerFilter}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>{t("runs.filters.allTriggers")}</SelectItem>
              {TRIGGERS.map((trigger) => (
                <SelectItem key={trigger} value={trigger}>
                  {t(`runs.trigger.${trigger}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {telegramChats.length > 0 && (
            <Select value={chatFilter} onValueChange={setChatFilter}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t("runs.filters.allTelegramChats")}</SelectItem>
                {telegramChats.map((chat) => (
                  <SelectItem key={chat.chat_id} value={String(chat.chat_id)}>
                    {telegramChatLabel(chat)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {loading ? (
          <Skeleton className="h-64 w-full" />
        ) : runs.length === 0 ? (
          <EmptyState
            icon={Activity}
            title={t("runs.empty.title")}
            description={t("runs.empty.description")}
          />
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">{t("runs.columns.status")}</th>
                  <th className="px-4 py-2 text-left font-medium">{t("runs.columns.trigger")}</th>
                  <th className="px-4 py-2 text-left font-medium">
                    {t("runs.columns.telegramChat")}
                  </th>
                  {isAdmin && (
                    <>
                      <th className="px-4 py-2 text-left font-medium">{t("runs.columns.steps")}</th>
                      <th className="px-4 py-2 text-left font-medium">
                        {t("runs.columns.toolCalls")}
                      </th>
                      <th className="px-4 py-2 text-left font-medium">{t("runs.columns.tokens")}</th>
                      <th className="px-4 py-2 text-left font-medium">{t("runs.columns.cost")}</th>
                    </>
                  )}
                  <th className="px-4 py-2 text-left font-medium">{t("runs.columns.duration")}</th>
                  <th className="px-4 py-2 text-left font-medium">
                    {isAdmin ? t("runs.columns.started") : t("runs.columns.created")}
                  </th>
                  {isAdmin && <th className="px-4 py-2" />}
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id} className="hover:bg-accent/40 border-t">
                    <td className="px-4 py-2">
                      <RunStatusBadge status={run.status} />
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant="outline">{t(`runs.trigger.${run.trigger}`)}</Badge>
                    </td>
                    <td className="px-4 py-2">
                      {run.telegram_chat ? (
                        <span className="inline-flex items-center gap-1 text-xs">
                          <Send className="text-muted-foreground size-3" />
                          {telegramChatLabel(run.telegram_chat)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    {isAdmin && (
                      <>
                        <td className="px-4 py-2">{run.iterations}</td>
                        <td className="px-4 py-2">{run.tool_call_count}</td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span title={t("runs.tokensTooltip")}>
                            {(run.prompt_tokens ?? 0).toLocaleString(locale)}↑{" / "}
                            {(run.completion_tokens ?? 0).toLocaleString(locale)}↓
                          </span>
                        </td>
                        <td className="px-4 py-2">{formatCostUsd(run.cost_usd)}</td>
                      </>
                    )}
                    <td className="px-4 py-2">{formatDuration(run.duration_ms)}</td>
                    <td className="text-muted-foreground px-4 py-2">
                      {formatDateTime(
                        isAdmin ? (run.started_at ?? run.created_at) : run.created_at,
                        locale,
                      )}
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-2 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/runs/${run.id}`)}
                        >
                          {t("runs.viewTrace")}
                          <ChevronRight className="size-4" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

function RunDetailView({ runId, onBack }: { runId: string; onBack: () => void }) {
  const t = useT()
  const { locale } = useLocale()
  const [trace, setTrace] = React.useState<RunTrace | null>(null)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    api
      .runTrace(runId)
      .then(setTrace)
      .catch((error) =>
        toast.error(error instanceof ApiError ? error.message : t("runs.detail.toasts.loadFailed")),
      )
      .finally(() => setLoading(false))
  }, [runId, t])

  if (loading) return <Skeleton className="m-6 h-64" />
  if (!trace) return null

  const { run, invocations, llm_calls } = trace
  const snapshot = run.agent_snapshot as Record<string, unknown>

  return (
    <>
      <PageHeader
        title={t("runs.detail.title")}
        description={t("runs.detail.description")}
        action={
          <Button variant="outline" onClick={onBack}>
            {t("runs.detail.back")}
          </Button>
        }
      />

      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-center gap-2">
          <RunStatusBadge status={run.status} />
          <Badge variant="outline">{t(`runs.trigger.${run.trigger}`)}</Badge>
          {run.telegram_chat && (
            <Badge variant="outline">
              <Send className="size-3" />
              {telegramChatLabel(run.telegram_chat)}
            </Badge>
          )}
          <Badge variant="secondary">{String(snapshot.model ?? "")}</Badge>
          <span className="text-muted-foreground text-sm">
            {t("runs.detail.stepsCount", { count: run.iterations })} ·{" "}
            {t("runs.detail.tokensIn", { count: (run.prompt_tokens ?? 0).toLocaleString(locale) })} ·{" "}
            {t("runs.detail.tokensOut", {
              count: (run.completion_tokens ?? 0).toLocaleString(locale),
            })}
            {(run.cached_tokens ?? 0) > 0 && (
              <>
                {" "}
                ·{" "}
                {t("runs.detail.tokensCached", {
                  count: run.cached_tokens!.toLocaleString(locale),
                })}
              </>
            )}{" "}
            · {t("runs.detail.tokensTotal", { count: run.total_tokens.toLocaleString(locale) })}
            {run.cost_usd != null && <> · {formatCostUsd(run.cost_usd)}</>} ·{" "}
            {formatDuration(run.duration_ms)}
          </span>
        </div>

        {run.error_message && (
          <Alert variant="destructive">
            <CircleAlert />
            <AlertTitle>{run.error_kind ?? t("runs.detail.runFailed")}</AlertTitle>
            <AlertDescription>{run.error_message}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("runs.detail.snapshotTitle")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-3 text-sm">{t("runs.detail.snapshotHint")}</p>
            <JsonViewer value={snapshot} className="max-h-64" />
          </CardContent>
        </Card>

        <div>
          <h2 className="mb-3 text-sm font-semibold">
            {t("runs.detail.llmCallsTitle", { count: llm_calls.length })}
          </h2>
          {llm_calls.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("runs.detail.noLlmCalls")}</p>
          ) : (
            <div className="space-y-2">
              {llm_calls.map((call) => (
                <Collapsible key={call.id} className="rounded-lg border">
                  <CollapsibleTrigger className="hover:bg-accent/40 flex w-full items-center gap-3 px-4 py-3 text-left">
                    <Brain className="size-4 shrink-0" />
                    <span className="text-sm font-medium">
                      {call.kind === "memory_extraction"
                        ? t("runs.detail.memoryExtraction")
                        : t("runs.detail.stepIndex", { n: call.iteration })}
                      {call.kind === "summary" ? ` · ${t("runs.detail.summary")}` : ""}
                    </span>
                    <Badge variant="outline">{call.request.model}</Badge>
                    <span className="text-muted-foreground text-xs">
                      {t("runs.detail.messageCount", { count: call.request.messages.length })} ·{" "}
                      {t("common.toolCount", { count: call.request.tools?.length ?? 0 })}
                    </span>
                    <ChevronRight className="text-muted-foreground ml-auto size-4" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="border-t px-4 py-3">
                    <div className="text-muted-foreground mb-1 text-xs font-medium">
                      {t("runs.detail.requestLabel")}
                    </div>
                    <JsonViewer value={call.request} defaultExpandDepth={2} className="max-h-96" />
                    {call.response && (
                      <>
                        <div className="text-muted-foreground mt-3 mb-1 text-xs font-medium">
                          {t("runs.detail.responseLabel")}
                        </div>
                        <JsonViewer value={call.response} defaultExpandDepth={2} className="max-h-96" />
                      </>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          )}
        </div>

        <div>
          <h2 className="mb-3 text-sm font-semibold">
            {t("runs.detail.toolCallsTitle", { count: invocations.length })}
          </h2>
          {invocations.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("runs.detail.noToolCalls")}</p>
          ) : (
            <div className="space-y-2">
              {invocations.map((invocation) => (
                <Collapsible
                  key={invocation.id}
                  className={cn(
                    "rounded-lg border",
                    invocation.is_error && "border-destructive/40",
                  )}
                >
                  <CollapsibleTrigger className="hover:bg-accent/40 flex w-full items-center gap-3 px-4 py-3 text-left">
                    {invocation.is_error ? (
                      <CircleAlert className="text-destructive size-4 shrink-0" />
                    ) : (
                      <Wrench className="size-4 shrink-0" />
                    )}
                    <span className="payload font-medium">{invocation.mcp_tool_name}</span>
                    <Badge variant="outline">{invocation.connection_slug}</Badge>
                    <span className="text-muted-foreground text-xs">
                      {t("runs.detail.stepIndex", { n: invocation.iteration })} ·{" "}
                      {formatDuration(invocation.duration_ms)}
                    </span>
                    {invocation.error_kind && (
                      <Badge variant="destructive">{invocation.error_kind}</Badge>
                    )}
                    {invocation.truncated && (
                      <Badge variant="warning">{t("runs.detail.truncated")}</Badge>
                    )}
                    <ChevronRight className="text-muted-foreground ml-auto size-4" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-3 border-t px-4 py-3">
                    <div>
                      <div className="text-muted-foreground mb-1 text-xs font-medium">
                        {t("runs.detail.argumentsLabel")}
                      </div>
                      {invocation.arguments_redacted ? (
                        <pre className="payload bg-muted/50 max-h-40 overflow-auto rounded-md p-3">
                          {t("runs.detail.redacted")}
                        </pre>
                      ) : (
                        <JsonViewer
                          value={invocation.arguments ?? {}}
                          defaultExpandDepth={2}
                          className="max-h-40"
                        />
                      )}
                    </div>
                    <div>
                      <div className="text-muted-foreground mb-1 text-xs font-medium">
                        {t("runs.detail.resultLabel")}
                      </div>
                      <JsonOrText
                        text={invocation.result_text ?? t("runs.detail.noResult")}
                        defaultExpandDepth={2}
                        className="max-h-72"
                      />
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
